import { Card } from 'antd'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'
import { AlertOutlined } from '@ant-design/icons'
import { SalesPipelineData } from './data'
import { defaultSalesPipelineData } from './data'
import useGetAnalyticsData from '@forwardflow/hooks/customHooks/marketing/useGetAnalyticsData'

interface SalesPipelineHealthProps {
   data?: SalesPipelineData
}

const SalesPipelineHealth: React.FC<SalesPipelineHealthProps> = ({
   data = defaultSalesPipelineData,
}) => {
   const { data: analyticsData } = useGetAnalyticsData()

   const pieColors = [
      '#FF6B6B', // Red
      '#4ECDC4', // Teal
      '#45B7D1', // Blue
      '#96CEB4', // Green
      '#FFEAA7', // Yellow
      '#DDA0DD', // Plum
      '#98D8C8', // Mint
      '#F7DC6F', // Light Yellow
      '#BB8FCE', // Light Purple
      '#85C1E9', // Light Blue
   ]

   // Helper functions to check data availability
   const hasConversionData = () => {
      return analyticsData?.totalPeopleWithAppointments && 
             analyticsData?.totalPeople && 
             analyticsData.totalPeople > 0
   }

   const hasLeadStageData = () => {
      return analyticsData?.leadStageCounts && 
             analyticsData.leadStageCounts.length > 0 &&
             analyticsData.totalPeople > 0
   }

   const hasPerformanceData = () => {
      return analyticsData?.totalLeadsLastDays && 
             analyticsData?.leadsWithAppointmentsSetLastDays &&
             analyticsData.totalLeadsLastDays > 0
   }

   const getPieChartData = () => {
      if (!hasLeadStageData()) return []
      
      return analyticsData.leadStageCounts
         .slice(1) // Skip first item if needed
         .flatMap((item: { stages: any[] }, groupIndex: number) =>
            item.stages.map((stage, stageIndex) => ({
               name: stage.name,
               value: parseFloat(
                  ((stage.totalLeads / analyticsData.totalPeople) * 100).toFixed(1)
               ),
               color: pieColors[
                  (groupIndex * item.stages.length + stageIndex) % pieColors.length
               ],
            }))
         )
         .filter((item: { name: string; value: number; color: string }) => item.value > 0) // Only include stages with data
   }

   const getConversionRate = () => {
      if (!hasConversionData()) return 0
      return ((analyticsData.totalPeopleWithAppointments / analyticsData.totalPeople) * 100).toFixed(2)
   }

   const getAppointmentRate = () => {
      if (!hasPerformanceData()) return 0
      return ((analyticsData.leadsWithAppointmentsSetLastDays / analyticsData.totalLeadsLastDays) * 100).toFixed(2)
   }

   const pieChartData = getPieChartData()

   // If no data is available, show a minimal version
   if (!hasConversionData() && !hasLeadStageData() && !hasPerformanceData()) {
      return (
         <div className="w-full mx-auto">
            {/* Header */}
            <div className="px-6 py-4 mb-4 text-white rounded-lg bg-primary-dark">
               <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12">
                     <div className="relative">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                           <div className="flex items-center justify-center w-8 h-8 bg-white rounded-md">
                              <div className="text-lg">📊</div>
                           </div>
                        </div>
                     </div>
                  </div>
                  <h2 className="text-lg font-medium">SALES PIPELINE HEALTH</h2>
               </div>
            </div>

            {/* No Data Message */}
            <div className="rounded-xl backdrop-blur-sm">
               <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 transition-all duration-200">
                  <div className="py-8 text-center">
                     <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-600 rounded-full">
                        <span className="text-2xl">📊</span>
                     </div>
                     <p className="text-gray-400">No pipeline data available to display.</p>
                  </div>
               </Card>
            </div>
         </div>
      )
   }

   return (
      <div className="w-full mx-auto">
         {/* Header */}
         <div className="px-6 py-4 mb-4 text-white rounded-lg bg-primary-dark">
            <div className="flex items-center gap-4">
               <div className="flex items-center justify-center w-12 h-12">
                  <div className="relative">
                     <div className="flex items-center justify-center w-10 h-10 rounded-lg shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                        <div className="flex items-center justify-center w-8 h-8 bg-white rounded-md">
                           <div className="text-lg">📊</div>
                        </div>
                     </div>
                  </div>
               </div>
               <h2 className="text-lg font-medium">SALES PIPELINE HEALTH</h2>
            </div>
         </div>

         {/* Cards Container */}
         <div className="rounded-xl backdrop-blur-sm">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
               {/* Left Column - Pro Tip Card */}
               <div className="lg:col-span-1">
                  <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 h-full transition-all duration-200 group hover:shadow-xl">
                     <div className="text-center">
                        <div className="mb-4">
                           <h3 className="text-sm font-semibold text-yellow-400">
                              Success Strategy
                           </h3>
                        </div>

                        <div className="flex items-center justify-center h-32 mb-4">
                           <div className="relative">
                              <div className="flex items-center justify-center w-20 h-20 rounded-lg shadow-lg bg-gradient-to-br from-yellow-500 to-yellow-600">
                                 <div className="flex items-center justify-center w-16 h-16 bg-white rounded-lg">
                                    <div className="text-3xl">💡</div>
                                 </div>
                              </div>
                              <div className="absolute flex items-center justify-center w-6 h-6 bg-yellow-600 rounded-full shadow-md -bottom-1 -right-1">
                                 <span className="text-xs text-white">✨</span>
                              </div>
                           </div>
                        </div>

                        <p className="text-sm leading-relaxed text-gray-300">
                           Consistent follow-up within the first 24 hours of lead capture can increase conversion rates by up to 391%. Focus on speed-to-lead metrics.
                        </p>
                     </div>
                  </Card>
               </div>

               {/* Right Column - Main Content */}
               <div className="space-y-6 lg:col-span-2">
                  {/* Conversion Analysis Card - Only show if conversion data exists */}
                  {hasConversionData() && (
                     <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 transition-all duration-200 group hover:shadow-xl">
                        <div className="mb-4">
                           <h3 className="mb-4 text-sm font-semibold text-center text-white">
                              Conversion Analysis
                           </h3>
                        </div>

                        <div className="flex items-start justify-between mb-4">
                           <div className="flex items-center gap-3">
                              <div className="relative">
                                 <div className="flex items-center justify-center w-12 h-12 rounded-lg shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
                                    <div className="flex items-center justify-center w-10 h-10 bg-white rounded-lg">
                                       <AlertOutlined className="text-lg !text-blue-600" />
                                    </div>
                                 </div>
                                 <div className="absolute flex items-center justify-center w-6 h-6 bg-blue-600 rounded-full shadow-md -top-1 -right-1">
                                    <span className="text-xs text-white">!</span>
                                 </div>
                              </div>
                              <div>
                                 <h4 className="font-semibold text-white">
                                    Is your team converting leads efficiently?
                                 </h4>
                              </div>
                           </div>
                           <div className="px-3 py-1 text-sm border rounded-full text-white bg-blue-500/20 border-blue-500/30">
                              {getConversionRate()}% conversion rate
                           </div>
                        </div>

                        <div className="p-4 mb-6 border rounded-lg border-blue-500/30 bg-blue-500/10">
                           <div className="flex items-start gap-3">
                              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 mt-1 bg-blue-500 rounded-full">
                                 <span className="text-sm text-white">!</span>
                              </div>
                              <p className="text-sm text-gray-300">
                                 Your conversion rate is above industry average. Consider scaling your successful strategies to maintain this performance.
                              </p>
                           </div>
                        </div>

                        {/* Lead Statistics - Only show if lead stage data exists */}
                        {hasLeadStageData() && (
                           <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                              <div>
                                 <div className="flex items-center justify-between mb-4">
                                    <h4 className="font-semibold text-white">
                                       {analyticsData.totalPeople} New Leads
                                    </h4>
                                 </div>

                                 <div className="space-y-3">
                                    {analyticsData.leadStageCounts.map((item: any, index: any) => (
                                       <div key={index} className="space-y-2">
                                          {/* Stage Group Header */}
                                          <div className="flex items-center justify-between">
                                             <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-white/80">
                                                   {item.stageGroup}
                                                </span>
                                             </div>
                                          </div>

                                          {/* Individual Stages */}
                                          {item.stages
                                             .filter((stage: { name: string; totalLeads: number }) => stage.totalLeads > 0) // Only show stages with leads
                                             .map((stage: { name: string; totalLeads: number }, stageIndex: number) => (
                                                <div key={stageIndex} className="flex items-center ml-3">
                                                   <div className="flex items-center gap-2">
                                                      <div className="w-2 h-2 bg-white rounded-full" />
                                                      <span className="text-sm text-white/80">
                                                         {stage.name}
                                                      </span>
                                                   </div>
                                                   <div className="flex items-center w-16 gap-3 ml-auto">
                                                      <span className="text-sm font-medium text-white">
                                                         {stage.totalLeads}
                                                      </span>
                                                   </div>
                                                   <div>
                                                      <span className="text-sm font-medium text-white/60">
                                                         {((stage.totalLeads / analyticsData.totalPeople) * 100).toFixed(2)}%
                                                      </span>
                                                   </div>
                                                </div>
                                             ))}
                                       </div>
                                    ))}
                                 </div>
                              </div>

                              {/* Pie Chart - Only show if there's data */}
                              {pieChartData.length > 0 && (
                                 <div className="flex items-center justify-center">
                                    <div className="w-48 h-48">
                                       <ResponsiveContainer width="100%" height="100%">
                                          <PieChart>
                                             <Pie
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={90}
                                                paddingAngle={2}
                                                dataKey="value"
                                             >
                                                {pieChartData.map((entry: any, index: any) => (
                                                   <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                             </Pie>
                                          </PieChart>
                                       </ResponsiveContainer>
                                    </div>
                                 </div>
                              )}
                           </div>
                        )}
                     </Card>
                  )}

                  {/* Performance Metrics - Only show if performance data exists */}
                  {hasPerformanceData() && (
                     <Card className="relative shadow-2xl bg-gradient-to-br from-[#2a2438] to-[#232033] border-[#3a3548] border-2 transition-all duration-200 group hover:shadow-xl">
                        <div className="mb-4">
                           <h3 className="text-sm font-semibold text-center text-yellow-400">
                              Performance Metrics
                           </h3>
                        </div>

                        <div className="p-4 mb-6 border rounded-lg bg-gray-500/10 border-gray-500/20">
                           <p className="mb-4 text-gray-300">
                              In the last 30 day period, you brought in{' '}
                              <strong className="text-white">
                                 {analyticsData.totalLeadsLastDays} New Leads
                              </strong>{' '}
                              and set{' '}
                              <strong className="text-white">
                                 appointments for {analyticsData.leadsWithAppointmentsSetLastDays}
                              </strong>{' '}
                              of them.
                           </p>
                           <div className="flex items-center gap-4">
                              <p className="text-gray-300">
                                 That's a{' '}
                                 <strong className="text-white">
                                    {getAppointmentRate()}%
                                 </strong>{' '}
                                 appointment rate, which is{' '}
                                 <strong
                                    className={
                                       data.performanceMetrics.isAboveAverage
                                          ? 'text-green-400'
                                          : 'text-red-400'
                                    }
                                 >
                                    {data.performanceMetrics.comparisonRate}%{' '}
                                    {data.performanceMetrics.isAboveAverage ? 'more' : 'less'}
                                 </strong>{' '}
                                 than Top Forward flow team averages.
                              </p>
                              <div
                                 className={`px-4 py-1 text-sm border rounded-full ${
                                    data.performanceMetrics.isAboveAverage
                                       ? 'text-green-400 bg-green-500/20 border-green-500/30'
                                       : 'text-red-400 bg-red-500/20 border-red-500/30'
                                 }`}
                              >
                                 {getAppointmentRate()}% appointment rate
                              </div>
                           </div>
                        </div>
                     </Card>
                  )}
               </div>
            </div>
         </div>
      </div>
   )
}

export default SalesPipelineHealth